/// <reference types="@types/google.maps" />

import { siteConfig } from "@/data/site";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";

export type LiveGoogleReview = {
  authorName: string;
  authorUri: string | null;
  authorPhotoUri: string | null;
  rating: number;
  text: string | null;
  relativePublishTime: string | null;
  publishTimeIso: string | null;
};

export type LiveGooglePlaceReviews = {
  displayName: string;
  formattedAddress: string;
  rating: number;
  userRatingCount: number;
  googleMapsUri: string;
  reviews: LiveGoogleReview[];
  attributions: Array<{ provider: string; providerUri: string | null }>;
};

export type GooglePlaceReviewLike = {
  authorAttribution: {
    displayName: string;
    uri: string | null;
    photoURI: string | null;
  } | null;
  publishTime: Date | null;
  rating: number | null;
  relativePublishTimeDescription: string | null;
  text: string | null;
};

export type GooglePlaceLike = {
  displayName?: string | null;
  formattedAddress?: string | null;
  rating?: number | null;
  userRatingCount?: number | null;
  googleMapsURI?: string | null;
  reviews?: GooglePlaceReviewLike[];
  attributions?: Array<{ provider: string | null; providerURI: string | null }>;
};

function normalizeIdentity(value: string) {
  return value.toLocaleLowerCase("en-US").replace(/[^a-z0-9]/g, "");
}

function isExpectedAddress(address: string) {
  const normalizedAddress = normalizeIdentity(address);
  return [
    siteConfig.address.streetAddress,
    siteConfig.address.addressLocality,
    siteConfig.address.addressRegion,
    siteConfig.address.postalCode,
  ].every((part) => normalizedAddress.includes(normalizeIdentity(part)));
}

function isValidRating(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 1 && value <= 5;
}

export function normalizePartitionStallsGooglePlace(place: GooglePlaceLike): LiveGooglePlaceReviews {
  const displayName = place.displayName?.trim() ?? "";
  const formattedAddress = place.formattedAddress?.trim() ?? "";

  if (normalizeIdentity(displayName) !== normalizeIdentity(siteConfig.name) || !isExpectedAddress(formattedAddress)) {
    throw new Error("Google returned a business identity that does not match Partition Stalls.");
  }

  if (!isValidRating(place.rating) || typeof place.userRatingCount !== "number" || place.userRatingCount < 0) {
    throw new Error("Google did not return a valid rating summary for Partition Stalls.");
  }

  const googleMapsUri = place.googleMapsURI?.trim() || siteConfig.googleReviews.read;
  const reviews = (place.reviews ?? []).flatMap<LiveGoogleReview>((review) => {
    const author = review.authorAttribution;
    if (!author?.displayName?.trim() || !isValidRating(review.rating)) return [];

    return [{
      authorName: author.displayName.trim(),
      authorUri: author.uri?.trim() || null,
      authorPhotoUri: author.photoURI?.trim() || null,
      rating: review.rating,
      text: review.text?.trim() || null,
      relativePublishTime: review.relativePublishTimeDescription?.trim() || null,
      publishTimeIso: review.publishTime instanceof Date && !Number.isNaN(review.publishTime.getTime())
        ? review.publishTime.toISOString()
        : null,
    }];
  });

  const attributions = (place.attributions ?? []).flatMap((attribution) => {
    const provider = attribution.provider?.trim();
    if (!provider) return [];
    return [{ provider, providerUri: attribution.providerURI?.trim() || null }];
  });

  return {
    displayName,
    formattedAddress,
    rating: place.rating,
    userRatingCount: Math.floor(place.userRatingCount),
    googleMapsUri,
    reviews,
    attributions,
  };
}

export async function fetchPartitionStallsGoogleReviews(): Promise<LiveGooglePlaceReviews> {
  const maps = await loadGoogleMaps();
  const { Place } = await maps.importLibrary("places") as google.maps.PlacesLibrary;
  const place = new Place({ id: siteConfig.googleReviews.placeId });

  await place.fetchFields({
    fields: [
      "displayName",
      "formattedAddress",
      "rating",
      "userRatingCount",
      "googleMapsURI",
      "reviews",
    ],
  });

  return normalizePartitionStallsGooglePlace(place);
}
